/**
 * Extracts the common manager/admin recipient display and auto-share logic
 * shared between ReportShareSheetContainer and NoteShareSheetContainer.
 *
 * Only MEMBER-role users auto-share with unshared managers — managers and
 * admins must share manually via the share sheet selection. This prevents
 * managers from flooding other managers with auto-shares on every submit.
 *
 * For MEMBER users, both containers: identify managers from org users, display
 * them as non-revocable recipients with a MANAGER badge, compute which managers
 * lack explicit share records, and share with them when the user submits.
 * For MANAGER/ADMIN users, manager display and filtering are skipped entirely
 * so the share sheet renders normally without manager-specific badges.
 * @module components/shared/useShareModal/useManagerShareRecipients
 */

import { useCallback, useMemo } from "react";

import type { AvailableUserDisplayData } from "./useShareModal";

import { useCurrentUser } from "@/features/auth";
import type { RecipientDisplayData } from "@/features/reports/components/molecules/ShareRecipientsList";
import { RoleName } from "@/generated/graphql";

/** Minimum shape a share record must have for manager filtering */
interface ShareRecordBase {
  readonly userId: string;
  readonly role: string;
}

/** Options for configuring the manager share recipients hook */
interface UseManagerShareRecipientsOptions<
  TShare extends ShareRecordBase = ShareRecordBase,
> {
  /** All organization users from the share modal */
  readonly allOrgUsers: readonly AvailableUserDisplayData[];
  /** User IDs that already have explicit share records */
  readonly existingShareUserIds: readonly string[];
  /** The modal's onShare callback to invoke alongside manager shares */
  readonly modalOnShare: () => Promise<unknown> | unknown;
  /** Share a single user by ID (callers wrap their entity-specific mutation) */
  readonly shareWithUser: (targetUserId: string) => Promise<unknown>;
  /** Visible shares to filter managers out of (optional for containers that display shares) */
  readonly visibleShares?: readonly TShare[];
}

/** Return value from useManagerShareRecipients */
interface UseManagerShareRecipientsReturn<
  TShare extends ShareRecordBase = ShareRecordBase,
> {
  /** Set of user IDs that are managers or admins */
  readonly managerUserIds: ReadonlySet<string>;
  /** Visible shares with managers filtered out */
  readonly nonManagerShares: readonly TShare[];
  /** Manager/admin display data for the recipients list */
  readonly managerRecipients: readonly RecipientDisplayData[];
  /** Manager IDs that don't have an explicit share record yet */
  readonly unsharedManagerIds: readonly string[];
  /** Shares with selected users and unshared managers in parallel */
  readonly shareWithUnsharedManagers: () => Promise<void>;
}

/**
 * Hook that computes manager recipient data and provides a share callback
 * that auto-includes unshared managers alongside the modal's selected users.
 * Auto-share with unshared managers is restricted to MEMBER-role users only;
 * managers/admins only share with explicitly selected users.
 * @param options - Configuration for manager share behavior
 * @param options.allOrgUsers - All organization users from the share modal
 * @param options.existingShareUserIds - User IDs with explicit share records
 * @param options.modalOnShare - The modal's onShare callback to invoke alongside manager shares
 * @param options.shareWithUser - Caller-provided function that shares with a single user by ID
 * @param options.visibleShares - Shares to filter managers from (for share list display)
 * @returns Manager display data, unshared IDs, and combined share callback
 */
export const useManagerShareRecipients = <
  TShare extends ShareRecordBase = ShareRecordBase,
>({
  allOrgUsers,
  existingShareUserIds,
  modalOnShare,
  shareWithUser,
  visibleShares = [] as unknown as readonly TShare[],
}: UseManagerShareRecipientsOptions<TShare>): UseManagerShareRecipientsReturn<TShare> => {
  const currentUser = useCurrentUser();

  /** Only members auto-share — managers/admins share manually via share sheet */
  const isMember = currentUser?.role?.name === RoleName.Member;

  const managerUserIds = useMemo(
    () =>
      new Set(
        allOrgUsers
          .filter(
            user =>
              user.orgRoleName === "MANAGER" || user.orgRoleName === "ADMIN"
          )
          .map(user => user.userId)
      ),
    [allOrgUsers]
  );

  const nonManagerShares = useMemo(
    () =>
      isMember
        ? visibleShares.filter(share => !managerUserIds.has(share.userId))
        : visibleShares,
    [visibleShares, managerUserIds, isMember]
  );

  const managerRecipients: readonly RecipientDisplayData[] = useMemo(
    () =>
      isMember
        ? allOrgUsers
            .filter(user => managerUserIds.has(user.userId))
            .map(user => ({
              userId: user.userId,
              displayName: user.displayName,
              initials: user.initials,
              role: "MANAGER",
            }))
        : [],
    [allOrgUsers, managerUserIds, isMember]
  );

  const unsharedManagerIds = useMemo(() => {
    if (!isMember) return [];
    const existingSet = new Set(existingShareUserIds);
    return [...managerUserIds].filter(id => !existingSet.has(id));
  }, [managerUserIds, existingShareUserIds, isMember]);

  const shareWithUnsharedManagers = useCallback(async () => {
    await Promise.all([
      modalOnShare(),
      ...unsharedManagerIds.map(targetUserId => shareWithUser(targetUserId)),
    ]);
  }, [modalOnShare, unsharedManagerIds, shareWithUser]);

  return {
    managerUserIds,
    nonManagerShares,
    managerRecipients,
    unsharedManagerIds,
    shareWithUnsharedManagers,
  };
};
